"use client";
import { SearchInput } from "@cmp-src/controls/SearchInput/SearchInput";
import { Dialog, DialogContent } from "@cmp-src/primitives/Dialog";
import { canScheduleOthers, cn } from "@gettactic/helpers";
import { useAuthenticated } from "@gettactic/hooks";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { X } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { useRouterPush } from "@/features/dashboard/helpers/router/useRouterPush";
import { useMapModalContext } from "@/features/map/components/modal";
import { EmptyState } from "./components/EmptyState";
import { KeyboardHints } from "./components/KeyboardHints";
import { SearchResults } from "./components/SearchResults";
import { INPUT_FOCUS_DELAY_MS } from "./constants";
import { GlobalSearchControls } from "./GlobalSearchControls";
import { useGlobalSearchData } from "./GlobalSearchDataProvider";
import { useGlobalSearch } from "./GlobalSearchProvider";
import { useDivisionCategories } from "./hooks/useDivisionCategories";
import { useKeyboardNavigation } from "./hooks/useKeyboardNavigation";
import { useSearchItemSelection } from "./hooks/useSearchItemSelection";
import { useSearchNavigation } from "./hooks/useSearchNavigation";
import { useSearchResults } from "./hooks/useSearchResults";

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

export function GlobalSearch({ isOpen, onClose }: GlobalSearchProps) {
  const { setSearchQuery: setQuery, searchQuery: query } = useGlobalSearch();
  const inputRef = useRef<HTMLInputElement>(null);
  const routerPush = useRouterPush();

  const {
    userContext: { currentOffice, authenticatedUser }
  } = useAuthenticated();

  const isAdmin = useMemo(
    () => canScheduleOthers(authenticatedUser),
    [authenticatedUser]
  );

  const {
    resources,
    people,
    offices,
    schedulesByUser,
    schedulesByResource,
    loading,
    hasNoResults: hasNoApiResults
  } = useGlobalSearchData();

  const divisionCategories = useDivisionCategories();

  const {
    filteredSettings,
    filteredActions,
    filteredOffices,
    reportSuggestions,
    hasNoResults,
    totalResults
  } = useSearchResults({
    query,
    resources,
    people,
    offices,
    hasNoApiResults,
    isAdmin,
    authenticatedUser
  });

  const navigation = useSearchNavigation({
    resources,
    offices,
    divisionCategories,
    currentOffice,
    onClose
  });

  const { handleSelectItem, handleSelectSetting } = useSearchItemSelection({
    filteredActions,
    resources,
    people,
    filteredOffices,
    filteredSettings,
    reportSuggestions,
    navigation
  });

  const { activeIndex, handleKeyDown, registerItemRef } = useKeyboardNavigation(
    {
      totalResults,
      hasNoResults,
      onSelect: handleSelectItem,
      onClose,
      onNoResultsAction: () => navigation.askTessaWithContext(query),
      isOpen
    }
  );

  const { closeModal: closeMapModal } = useMapModalContext();

  useEffect(() => {
    if (isOpen) {
      closeMapModal();
      setTimeout(() => inputRef.current?.focus(), INPUT_FOCUS_DELAY_MS);
    }
  }, [isOpen, closeMapModal]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="flex h-[80vh] max-h-[80vh] max-w-2xl flex-col gap-0 overflow-hidden rounded-brand-xl p-0"
        overlayProps={{ className: "bg-black/50" }}
        customCloseButton={
          <button
            type="button"
            onClick={onClose}
            className="absolute top-[15px] right-3 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        }
      >
        <div className="flex h-full min-h-0 flex-col" onKeyDown={handleKeyDown}>
          <div className="flex-shrink-0 border-neutral-100 border-b">
            <SearchInput
              query={query}
              setQuery={setQuery}
              ref={inputRef}
              className={"w-full border-0 py-3 placeholder-neutral-700"}
              placeholder={"Search resources, people, settings…"}
              withFocusRing={false}
              enableClearButton={false}
              preventAutoLowerCase
              fullWidth
            />
          </div>

          <GlobalSearchControls />

          <ScrollArea.Root
            type="hover"
            scrollHideDelay={500}
            className="flex-1 overflow-hidden"
          >
            <ScrollArea.Viewport className="[&>div]:!block h-full w-full">
              <div className="p-2">
                {loading || (hasNoResults && reportSuggestions.length === 0) ? (
                  <EmptyState isLoading={loading} />
                ) : (
                  <SearchResults
                    filteredActions={filteredActions}
                    resources={resources}
                    people={people}
                    filteredOffices={filteredOffices}
                    filteredSettings={filteredSettings}
                    reportSuggestions={reportSuggestions}
                    schedulesByResource={schedulesByResource}
                    schedulesByUser={schedulesByUser}
                    divisionCategories={divisionCategories}
                    activeIndex={activeIndex}
                    registerItemRef={registerItemRef}
                    onNavigateToResource={navigation.navigateToResource}
                    onNavigateToPerson={navigation.navigateToPerson}
                    onNavigateToDedicatedResource={
                      navigation.navigateToDedicatedResource
                    }
                    onNavigateToOffice={navigation.navigateToOffice}
                    onEditOffice={async (officeId) => {
                      await routerPush(
                        `/settings/offices/building/${officeId}`
                      );
                      onClose();
                    }}
                    onReserveOffice={navigation.reserveOffice}
                    onSelectSetting={handleSelectSetting}
                  />
                )}
              </div>
            </ScrollArea.Viewport>

            <ScrollArea.Scrollbar
              orientation="vertical"
              className={cn(
                "z-50 flex w-3 bg-neutral-50/50 p-[2px] hover:bg-neutral-100"
              )}
            >
              <ScrollArea.Thumb className="relative w-[4px] flex-1 rounded-full bg-neutral-300 hover:bg-neutral-400" />
            </ScrollArea.Scrollbar>
          </ScrollArea.Root>

          <KeyboardHints />
        </div>
      </DialogContent>
    </Dialog>
  );
}
